import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { formatCurrency } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Inject,
  input,
  Input,
  LOCALE_ID,
  Output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import * as _ from 'lodash';

import {
  actionTypes,
  CasualTournamentDetails,
  columnTypes,
  CurrentUser,
  EventSchedule,
  IColumnModel,
  IElementModel,
  ITableConfig,
  UniLevelDetail,
} from '@fantaz/shared/data-access/models';

@Component({
  selector: 'fz-table-view',
  templateUrl: './table-view.component.html',
  styleUrls: ['./table-view.component.scss'],
})
export class TableViewComponent {
  #destroyRef = inject(DestroyRef);
  #breakpointObserver = inject(BreakpointObserver);

  isAuthenticated = input<boolean>(false);

  @Input() schedules: EventSchedule[];

  @Input() tournamentDetails: CasualTournamentDetails | null;

  @Output() playSchedule: EventEmitter<EventSchedule> =
    new EventEmitter<EventSchedule>();

  @Output() shareSchedule: EventEmitter<EventSchedule> =
    new EventEmitter<EventSchedule>();

  @Output() viewSchedule: EventEmitter<EventSchedule> =
    new EventEmitter<EventSchedule>();

  displayedColumns: IColumnModel[] = [
    {
      id: 'tournament',
      title: 'Tournament name',
      type: () => columnTypes.CUSTOM,
      mapping: (element: any) => '',
      customElement: (element: any) => {
        if (!element?.toPlayNow) {
          return '<div class="loader"></div>';
        }

        return `
        <div>
          <div class="mat-body-large fw--500">${
            element?.toPlayNow?.eventCode ?? ''
          }</div>
          <div class="mat-body-small">${element?.toPlayNow?.eventName}</div>
        </div>
        `;
      },
      tooltip: () => null,
    },
    {
      id: 'seats',
      title: 'Seats',
      type: () => columnTypes.DEFAULT,
      mapping: (element: any) => element.totalEntryCount,
      tooltip: () => null,
    },
    {
      id: 'dollarAmount',
      title: 'Entry Fee',
      type: () => columnTypes.CURRENCY,
      mapping: (element: any) => element.entryAmount,
      tooltip: () => null,
    },
    {
      id: 'prizeDollarAmount',
      title: '1st Prize',
      type: () => columnTypes.CURRENCY,
      mapping: (element: any) => {
        if (element.firstPrize?.prizeValue) {
          return element.firstPrize?.prizeValue;
        }
        const prizeDetails = element.prizeDetails.find(
          (prize: any) => prize.rank === 1,
        );

        if (prizeDetails?.prizeValue) {
          return prizeDetails?.prizeValue;
        }

        return '';
      },
      tooltip: (element: any) => {
        if (element.prizeDetails?.length > 1) {
          let prizes = '';
          const prizeDetails = _.cloneDeep(element.prizeDetails).sort(
            (a: any, b: any) => a.rank - b.rank,
          );
          for (let i = 0; i < prizeDetails.length; i++) {
            prizes +=
              this.getRankWithOrdinal(prizeDetails[i].rank) +
              ' Place: ' +
              this.formatAmount(prizeDetails[i].prizeValue) +
              (i === prizeDetails.length - 1 ? '' : ',') +
              '\n';
          }
          return prizes;
        } else return null;
      },
    },
    {
      id: 'referralAmount',
      title: 'ZBO Referral\n' + '(amount per user)',
      type: () => columnTypes.CURRENCY,
      class: 'fw--500',
      mapping: (element: any) => {
        for (let i = 0; i < element.uniLevelDetails.length; i++) {
          if (element.uniLevelDetails[i].level === 1) {
            return element.uniLevelDetails[i].amountPerUser;
          }
        }
      },
      tooltip: () => null,
    },
    {
      id: 'toPlayNow',
      title: 'To Play Now',
      type: () => columnTypes.CUSTOM,
      mapping: (element: any) => '',
      tooltip: () => null,
      customElement: (element: any) => {
        if (!element?.toPlayNow) {
          return '<div class="loader"></div>';
        }

        const seats = element?.toPlayNow?.seats;
        const used = element?.toPlayNow?.participants?.length;
        const available = seats - used;

        let player = '';

        if (
          this.isAuthenticated() &&
          element?.toPlayNow?.participants?.length > 0
        ) {
          player = `
            <div class="mat-body-small">
              1st place <span class="font-color--primary"> ${element?.toPlayNow?.participants[0]?.player}<span>
            </div>
          `;
        }

        if (used > 0) {
          if (seats > 5001) {
            return `
              <div>
                <div class="mat-body-large">Unlimited seats</div>
                ${player}
              </div>
            `;
          }

          return `
            <div>
              <div class="mat-body-large">${available} available seat(${used}/${seats})</div>
              ${player}
            </div>
          `;
        }

        return `
          <div>
            <div class="mat-body-large">All seats available</div>
            <div class="mat-body-small">Play to be the first place!</div>
          </div>
        `;
      },
    },
    {
      id: 'actions',
      title: 'Actions',
      type: () => columnTypes.ACTIONS,
      mapping: (element: any) => null,
      tooltip: () => null,
      tournament: () => this.tournamentDetails,
      eventSchedule: (element: any) => element,
      playButtonText: () => null,
      passUuid: () => null,
      actionsToShow: () => [
        actionTypes.PLAY,
        actionTypes.SHARE,
        actionTypes.DETAILS,
      ],
    },
  ];

  configuration: ITableConfig = {
    headersTooltip: {
      referralAmount:
        'If you have paid your ZBO subscription, you will receive a referral every time a user you invited plays a tournament.',
    },
    tableName: 'tournament-details',
    pagination: {
      paginationTotal: 1,
      sizeQueryParam: 'pageSize',
      indexQueryParam: 'pageIndex',
      paginationSizeOptions: [10],
    },
  };

  data: any;

  @Input()
  currentUser: CurrentUser | null | undefined;

  mobileMatches = [Breakpoints.XSmall, Breakpoints.Small];
  isMobile = signal<boolean>(
    this.#breakpointObserver.isMatched(this.mobileMatches),
  );

  constructor(@Inject(LOCALE_ID) public locale: string) {
    this.#breakpointObserver
      .observe(this.mobileMatches)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((result) => {
        this.isMobile.set(result.matches);
      });
  }

  onShare(schedule: EventSchedule) {
    this.shareSchedule.emit(schedule);
  }
  onView(schedule: EventSchedule) {
    this.viewSchedule.emit(schedule);
  }

  onElementClicked(element: IElementModel) {
    this.onView(element.data);
  }

  findCorrectLevel(uniLevelDetails: UniLevelDetail[]) {
    if (uniLevelDetails) {
      for (let i = 0; i < uniLevelDetails.length; i++) {
        if (uniLevelDetails[i].level === 1) {
          return uniLevelDetails[i];
        }
      }
    }
    return null;
  }

  referralBonus(uniLevelDetails: UniLevelDetail[]): number | undefined {
    return this.findCorrectLevel(uniLevelDetails)?.amountPerUser;
  }

  floorFigure(figure: number): string {
    const decimals = 2;
    const d = Math.pow(10, decimals);
    return (parseInt(`${figure * d}`) / d).toFixed(decimals);
  }

  // TODO: move it to a service if is required to use in another place
  formatAmount(amount: number): string {
    if (amount) {
      const f = formatCurrency(
        parseFloat(this.floorFigure(amount)),
        this.locale,
        '',
        '',
        '1.2-2',
      );
      return f.toString();
    }

    return '0';
  }

  getRankWithOrdinal(rank: number) {
    const s = ['th', 'st', 'nd', 'rd'],
      v = rank % 100;
    return rank + (s[(v - 20) % 10] || s[v] || s[0]);
  }
}
